import { INVENTORIZATION_BLOCK_IDS } from '@/features/inventorization/domain/inventorization';
import {
  getInventorizationPositionBlockStatus,
  validateInventorizationPosition,
} from '@/features/inventorization/domain/inventorization-position-validation';
import type { CaptureSession } from '@/shared/capture/capture-session';

export {
  getInventorizationPositionBlockStatus,
  validateInventorizationPosition,
} from '@/features/inventorization/domain/inventorization-position-validation';

export const GPS_CAPTURE_STATES = {
  captured: 'captured',
  unavailable: 'unavailable',
} as const;

export type GpsCaptureState = '' | (typeof GPS_CAPTURE_STATES)[keyof typeof GPS_CAPTURE_STATES];

export const FLOORPLAN_PLACEMENT_STATES = {
  notAvailable: 'not_available',
  notPlaced: 'not_placed',
  placed: 'placed',
} as const;

export type FloorplanPlacementState = '' | (typeof FLOORPLAN_PLACEMENT_STATES)[keyof typeof FLOORPLAN_PLACEMENT_STATES];

export interface InventorizationPosition {
  building: string;
  buildingId?: number | string | null;
  floor: string;
  floorplanBlobId?: string | null;
  floorplanLat: number | null;
  floorplanLng: number | null;
  floorplanNotPlacedReason: string;
  floorplanState: FloorplanPlacementState;
  gpsLat: number | null;
  gpsLng: number | null;
  gpsState: GpsCaptureState;
  gpsUnavailableReason: string;
}

export function readInventorizationPosition(session: CaptureSession): InventorizationPosition {
  const inventorization = session.data.inventorization as { position?: Partial<InventorizationPosition> } | undefined;
  const position = inventorization?.position ?? {};

  return {
    building: position.building ?? '',
    buildingId: readNullableNumberOrString(position.buildingId),
    floor: position.floor ?? '',
    floorplanBlobId: readNullableString(position.floorplanBlobId),
    floorplanLat: position.floorplanLat ?? null,
    floorplanLng: position.floorplanLng ?? null,
    floorplanNotPlacedReason: position.floorplanNotPlacedReason ?? '',
    floorplanState: readFloorplanState(position.floorplanState),
    gpsLat: position.gpsLat ?? null,
    gpsLng: position.gpsLng ?? null,
    gpsState: readGpsState(position.gpsState),
    gpsUnavailableReason: position.gpsUnavailableReason ?? '',
  };
}

export function updateInventorizationPosition(
  session: CaptureSession,
  patch: Partial<InventorizationPosition>,
  updatedAt: string,
): CaptureSession {
  const position = {
    ...readInventorizationPosition(session),
    ...patch,
  };
  const issues = validateInventorizationPosition(position);

  return {
    ...session,
    blockStatuses: {
      ...session.blockStatuses,
      [INVENTORIZATION_BLOCK_IDS.position]: getInventorizationPositionBlockStatus(position),
    },
    data: {
      ...session.data,
      inventorization: {
        ...(session.data.inventorization as object),
        position,
      },
    },
    updatedAt,
    validationErrors: [
      ...session.validationErrors.filter(issue => issue.blockId !== INVENTORIZATION_BLOCK_IDS.position),
      ...issues,
    ],
  };
}

function readNullableString(value: unknown) {
  const text = typeof value === 'string' ? value.trim() : '';

  return text || null;
}

function readNullableNumberOrString(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  const text = readNullableString(value);

  return text ? text : null;
}

function readGpsState(value: unknown): GpsCaptureState {
  return value === GPS_CAPTURE_STATES.captured || value === GPS_CAPTURE_STATES.unavailable ? value : '';
}

function readFloorplanState(value: unknown): FloorplanPlacementState {
  return value === FLOORPLAN_PLACEMENT_STATES.placed ||
    value === FLOORPLAN_PLACEMENT_STATES.notPlaced ||
    value === FLOORPLAN_PLACEMENT_STATES.notAvailable
    ? value
    : '';
}
